/**
 * Where a refused control's remedy is (I-1914, Jay: "there should be a simple way to click through
 * to the required setting if viewing or changing that is permitted"). A refusal's reason may carry
 * one: the web's refused control then opens the reason with a link to the place (`gateControl.ts`,
 * `RefusalDestination.tsx`), and a surface that cannot go there names the place in words.
 *
 * ⛔ `access` is what the viewer may do at the place, decided by whoever builds the reason. `none`
 * draws no link, only the reason, as a refusal without a destination does.
 */
export interface IRefusalDestination {
  /** The app path of the place, with the `#anchor` of its section when it has one. */
  readonly path: string;
  /** The place's own name, as its heading says it: "Starting agent sessions". */
  readonly label: string;
  /** The whole place in words, for a surface that cannot open it: "Infrastructure → This machine → …". */
  readonly place: string;
  /** Only the Mac app draws this place: a browser names it and opens the app instead. */
  readonly macApp: boolean;
  /**
   * The link that opens this very place in the Mac app from a browser, where the app routes one
   * (`DESKTOP_RETURN_START_SETTINGS_URL`). Without it a browser brings the app forward only.
   */
  readonly appUrl?: string;
  /**
   * ASF-19: `place` and `label` as catalogue keys, where the place is one the app names in every
   * language; a surface says them in the reader's, and `place`/`label` stay the English.
   */
  readonly placeKeys?: readonly string[];
  readonly labelKey?: string;
  readonly access: 'view' | 'change' | 'none';
}

/** Whether a destination is one the viewer may be sent to. */
export function refusalDestinationOpen(
  destination: IRefusalDestination | null | undefined,
): destination is IRefusalDestination {
  return Boolean(destination && destination.access !== 'none');
}
